import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { lambdaService, QueryHistoryItem } from '../../services/lambdaService';
import { toast } from 'sonner';
import { 
  Clock, 
  Trash2, 
  Bug, 
  Play,
  Sparkles,
  AlertTriangle,
  User,
  Bot,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  History
} from 'lucide-react';

interface QueryHistoryProps {
  onQuerySelect: (query: string) => void;
  selectedFile?: string;
  refreshTrigger?: number; // Timestamp to trigger refresh
}

// Helper function to clean up display names - hide internal implementation details
const getDisplayName = (fileName: string): string => {
  if (!fileName) return fileName;
  let name = fileName;
  // Remove common extensions
  name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
  // Remove double extensions like .csv.parquet
  name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
  // Remove timestamp prefix (e.g., "1765940893209-")
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  // Remove date-time suffix (e.g., "_20231215_143022")
  name = name.replace(/_\d{8}_\d{6}$/, '');
  // Remove "(X files)" or "(X parts)" suffixes
  name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
  // Remove "_part_X" or "_chunk_X" suffixes
  name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
  // Remove trailing underscores or hyphens
  name = name.replace(/[_-]+$/, '');
  return name;
};

interface AggregatedQuery {
  query_text: string;
  query_hash: string;
  runs: QueryHistoryItem[];
  latest_timestamp: number;
  success_count: number;
  total_runs: number;
  latest_prompt?: string;
  latest_model_info?: string;
}

export const QueryHistory: React.FC<QueryHistoryProps> = ({ onQuerySelect, selectedFile, refreshTrigger }) => {
  const [queries, setQueries] = useState<QueryHistoryItem[]>([]);
  const [aggregatedQueries, setAggregatedQueries] = useState<AggregatedQuery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDebugInfo, setSelectedDebugInfo] = useState<any>(null);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [lastKey, setLastKey] = useState<string | undefined>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(new Set());
  const [expandedQueryCards, setExpandedQueryCards] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadQueryHistory();
  }, [selectedFile]); // Reload when selected file changes

  useEffect(() => {
    // Aggregate queries when the queries list changes
    aggregateQueries();
  }, [queries, selectedFile]);

  useEffect(() => {
    // Auto-refresh when refreshTrigger changes
    if (refreshTrigger) {
      // console.log('🔄 Auto-refreshing query history after query execution');
      const timeoutId = setTimeout(() => {
        loadQueryHistory();
      }, 5000); // 5 second delay

      return () => clearTimeout(timeoutId);
    }
  }, [refreshTrigger]);

  const generateQueryHash = (queryText: string): string => {
    // Simple hash function to group similar queries
    return queryText.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  const aggregateQueries = () => {
    const queryMap = new Map<string, AggregatedQuery>();
    
    // No need to filter by file here since backend now does it
    const filteredQueries = queries;

    // First pass: collect all queries and find AI generation info
    const aiQueryInfo = new Map<string, { prompt?: string; model_info?: string; timestamp: number }>();
    
    filteredQueries.forEach(query => {
      if (query.query_type === 'ai_generated') {
        const hash = generateQueryHash(query.query_text);
        aiQueryInfo.set(hash, {
          prompt: query.prompt,
          model_info: query.model_info,
          timestamp: query.timestamp
        });
      }
    });

    // Second pass: aggregate all queries (including both AI and user executions)
    filteredQueries.forEach(query => {
      const hash = generateQueryHash(query.query_text);
      const aiInfo = aiQueryInfo.get(hash);
      
      if (queryMap.has(hash)) {
        const existing = queryMap.get(hash)!;
        existing.runs.push(query);
        existing.total_runs++;
        if (query.success) existing.success_count++;
        if (query.timestamp > existing.latest_timestamp) {
          existing.latest_timestamp = query.timestamp;
          // Preserve AI info even for user executions
          existing.latest_prompt = query.prompt || aiInfo?.prompt || existing.latest_prompt;
          existing.latest_model_info = query.model_info || aiInfo?.model_info || existing.latest_model_info;
        }
      } else {
        queryMap.set(hash, {
          query_text: query.query_text,
          query_hash: hash,
          runs: [query],
          latest_timestamp: query.timestamp,
          success_count: query.success ? 1 : 0,
          total_runs: 1,
          // Use AI info if available, otherwise use query info
          latest_prompt: query.prompt || aiInfo?.prompt,
          latest_model_info: query.model_info || aiInfo?.model_info
        });
      }
    });

    // Sort by latest timestamp
    const aggregated = Array.from(queryMap.values()).sort(
      (a, b) => b.latest_timestamp - a.latest_timestamp
    );
    
    setAggregatedQueries(aggregated);
  };

  const loadQueryHistory = async (loadMore: boolean = false) => {
    setIsLoading(true);
    try {
      // Pass selectedFile to get file-specific history from the backend
      const response = await lambdaService.getQueryHistory(50, loadMore ? lastKey : undefined, selectedFile || undefined);
      
      if (response.success && response.queries) {
        if (loadMore) {
          setQueries(prev => [...prev, ...response.queries!]);
        } else {
          setQueries(response.queries);
        }
        setLastKey(response.last_key);
        // console.log(`📊 Loaded ${response.queries.length} queries for file: ${selectedFile || 'all files'}`);
      } else {
        toast.error(`Failed to load query history: ${response.error}`);
      }
    } catch (error) {
      // console.error('Error loading query history:', error);
      toast.error('Failed to load query history');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleQueryExpansion = (queryHash: string) => {
    setExpandedQueries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(queryHash)) {
        newSet.delete(queryHash);
      } else {
        newSet.add(queryHash);
      }
      return newSet;
    });
  };

  const toggleQueryCardExpansion = (queryHash: string) => {
    setExpandedQueryCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(queryHash)) {
        newSet.delete(queryHash);
      } else {
        newSet.add(queryHash);
      }
      return newSet;
    });
  };

  const deleteQuery = async (queryId: string) => {
    try {
      const response = await lambdaService.deleteQueryHistory(queryId);
      
      if (response.success) {
        setQueries(prev => prev.filter(q => q.query_id !== queryId));
        toast.success('Query deleted from history');
      } else {
        toast.error(`Failed to delete query: ${response.error}`);
      }
    } catch (error) {
      // console.error('Error deleting query:', error);
      toast.error('Failed to delete query');
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getModelBadgeColor = (modelInfo?: string) => {
    if (!modelInfo) return 'secondary';
    
    if (modelInfo.includes('GPT-5')) {
      if (modelInfo.includes('minimal')) return 'success';
      if (modelInfo.includes('high')) return 'warning';
      return 'info';
    }
    
    if (modelInfo.includes('Claude 3 Haiku')) return 'success';
    if (modelInfo.includes('Claude 3.5 Sonnet')) return 'info';
    if (modelInfo.includes('Claude 3 Opus')) return 'warning';
    
    return 'secondary';
  };

  const truncateQuery = (query: string, maxLength: number = 60) => {
    if (query.length <= maxLength) return query;
    return query.substring(0, maxLength) + '...';
  };

  return (
    <>
      <div className={`${isCollapsed ? 'flex-shrink-0' : 'flex-1'} flex flex-col border-b border-border overflow-hidden`}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 h-6 w-6"
              >
                {isCollapsed ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronUp className="h-3 w-3" />
                )}
              </Button>
              <div>
                <h2 className="text-sm font-semibold flex items-center space-x-2">
                  <History className="h-4 w-4" />
                  <span>Query History</span>
                </h2>
                {!isCollapsed && (
                  <p className="text-xs text-muted-foreground">
                    {selectedFile 
                      ? `Queries for ${getDisplayName(selectedFile)}`
                      : 'Your recent queries and AI generations'
                    }
                  </p>
                )}
              </div>
            </div>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadQueryHistory()}
                disabled={isLoading}
                className="p-1 h-6 w-6"
              >
                <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </div>
        
        {!isCollapsed && (
          <div className="p-4 flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {aggregatedQueries.length === 0 && !isLoading ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">No queries yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Run queries to see history here
                    </p>
                  </div>
                ) : (
                  aggregatedQueries.map((aggregatedQuery) => {
                    const isExpanded = expandedQueries.has(aggregatedQuery.query_hash);
                    const isCardExpanded = expandedQueryCards.has(aggregatedQuery.query_hash);
                    const latestRun = aggregatedQuery.runs[0];
                    const hasMultipleRuns = aggregatedQuery.total_runs > 1;
                    
                    return (
                      <div key={aggregatedQuery.query_hash} className="space-y-2">
                        {/* Main aggregated query card */}
                        <div
                          className={`border transition-all ${
                            aggregatedQuery.success_count > 0
                              ? 'border-green-500/10 hover:bg-green-500/5 bg-green-500/2' 
                              : 'border-red-500/10 hover:bg-red-500/5 bg-red-500/2'
                          } rounded-md`}
                        >
                          {/* Collapsed Header - Always visible - Single row */}
                          <div 
                            className="px-3 py-2 cursor-pointer flex items-center justify-between"
                            onClick={() => toggleQueryCardExpansion(aggregatedQuery.query_hash)}
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                {latestRun.query_type === 'ai_generated' ? (
                                  <Bot className={`h-3 w-3 ${aggregatedQuery.success_count > 0 ? 'text-green-600' : 'text-red-600'}`} />
                                ) : (
                                  <User className={`h-3 w-3 ${aggregatedQuery.success_count > 0 ? 'text-blue-600' : 'text-red-600'}`} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex items-center space-x-2">
                                {aggregatedQuery.latest_prompt ? (
                                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                    "{aggregatedQuery.latest_prompt}"
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-900 dark:text-gray-100">
                                    Manual Query
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500 flex-shrink-0">{formatTimestamp(aggregatedQuery.latest_timestamp)}</span>
                                {hasMultipleRuns && (
                                  <>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500 flex-shrink-0">{aggregatedQuery.total_runs} runs</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onQuerySelect(aggregatedQuery.query_text);
                                }}
                                className="p-1 h-5 w-5"
                                title="Use this query"
                              >
                                <Play className="h-2.5 w-2.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-5 w-5"
                              >
                                {isCardExpanded ? (
                                  <ChevronUp className="h-2.5 w-2.5" />
                                ) : (
                                  <ChevronDown className="h-2.5 w-2.5" />
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Expandable Details - Hidden by default */}
                          {isCardExpanded && (
                            <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700">
                              <div className="pt-3 space-y-3">
                                {/* Model info and file name */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">{getDisplayName(latestRun.file_name)}</span>
                                    {aggregatedQuery.latest_model_info && (
                                      <>
                                        <span className="text-xs text-gray-400">•</span>
                                        <Badge variant={getModelBadgeColor(aggregatedQuery.latest_model_info)} className="text-xs py-0 px-1">
                                          {aggregatedQuery.latest_model_info}
                                        </Badge>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Query preview */}
                                <div>
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Query</div>
                                  <code className="mono text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border block overflow-x-auto">
                                    {aggregatedQuery.query_text}
                                  </code>
                                </div>

                                {/* Success rate for multiple runs */}
                                {hasMultipleRuns && (
                                  <div>
                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Success Rate</div>
                                    <Badge variant="outline" className="text-xs">
                                      {aggregatedQuery.success_count}/{aggregatedQuery.total_runs} successful runs
                                    </Badge>
                                  </div>
                                )}

                                {/* Execution details */}
                                <div>
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Latest Execution</div>
                                  <div className="text-xs text-gray-500">
                                    {latestRun.execution_time}ms • {latestRun.result_count} rows
                                  </div>
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center space-x-1 pt-2">
                                  {latestRun.query_type === 'ai_generated' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDebugInfo(latestRun);
                                        setDebugModalOpen(true);
                                      }}
                                      className="p-1 h-6 w-6"
                                      title="Debug info"
                                    >
                                      <Bug className="h-3 w-3" />
                                    </Button>
                                  )}
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteQuery(latestRun.query_id);
                                    }}
                                    className="p-1 h-6 w-6 text-red-600 hover:text-red-800"
                                    title="Delete from history"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>

                                {/* Multiple runs expansion */}
                                {hasMultipleRuns && (
                                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">All Runs</div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleQueryExpansion(aggregatedQuery.query_hash);
                                        }}
                                        className="p-1 h-6 w-6"
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="h-3 w-3" />
                                        ) : (
                                          <ChevronDown className="h-3 w-3" />
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Expanded runs */}
                        {isExpanded && hasMultipleRuns && (
                          <div className="ml-6 space-y-2 mt-3">
                            {aggregatedQuery.runs.slice(1).map((run, index) => (
                              <div
                                key={run.query_id}
                                className={`p-3 rounded-lg border text-xs ${
                                  run.success 
                                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                                    : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-muted-foreground font-medium">
                                      {formatTimestamp(run.timestamp)}
                                    </span>
                                    <Badge variant={run.success ? 'success' : 'destructive'} className="text-xs">
                                      {run.success ? 'Success' : 'Failed'}
                                    </Badge>
                                  </div>
                                  <div className="text-muted-foreground font-mono">
                                    {run.execution_time}ms • {run.result_count} rows
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
            </div>
          </div>
          
            {lastKey && (
              <div className="p-2 border-t flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadQueryHistory(true)}
                  disabled={isLoading}
                  className="w-full text-xs"
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Debug Modal */}
      <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
        <DialogContent className="w-[80vw] max-w-[80vw] h-[80vh] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Query Debug Information</DialogTitle>
            <DialogDescription>
              Complete raw lambda output and debug information
            </DialogDescription>
          </DialogHeader>

          {selectedDebugInfo && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Quick Summary */}
              <div className="bg-muted p-3 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                    <div className="mt-1">
                      <Badge variant={selectedDebugInfo.success ? 'success' : 'destructive'}>
                        {selectedDebugInfo.success ? 'Success' : 'Failed'}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Execution:</span>
                    <div className="mt-1 text-gray-900 dark:text-gray-100">
                      {selectedDebugInfo.execution_time}ms
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Results:</span>
                    <div className="mt-1 text-gray-900 dark:text-gray-100">
                      {selectedDebugInfo.result_count} rows
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">File:</span>
                    <div className="mt-1 text-gray-900 dark:text-gray-100 truncate" title={getDisplayName(selectedDebugInfo.file_name)}>
                      {getDisplayName(selectedDebugInfo.file_name)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Prompt (if available) */}
              {selectedDebugInfo.prompt && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Original Prompt</h3>
                  <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      "{selectedDebugInfo.prompt}"
                    </p>
                  </div>
                </div>
              )}

              {/* Generated Query */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Generated Query</h3>
                <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-words">
                  <code>{selectedDebugInfo.query_text}</code>
                </pre>
              </div>

              {/* Model Information */}
              {selectedDebugInfo.model_info && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Model Information</h3>
                  <div className="bg-muted p-3 rounded-lg">
                    <Badge variant={getModelBadgeColor(selectedDebugInfo.model_info)}>
                      {selectedDebugInfo.model_info}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Error Message (if any) */}
              {selectedDebugInfo.error_message && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-red-600">Error Details</h3>
                  <pre className="bg-red-50 dark:bg-red-950/20 p-3 rounded-lg text-xs whitespace-pre-wrap break-words text-red-800 dark:text-red-200">
                    <code>{selectedDebugInfo.error_message}</code>
                  </pre>
                </div>
              )}

              {/* Raw Lambda Output */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Complete Raw Debug Data</h3>
                <pre className="bg-gray-900 dark:bg-gray-950 text-green-400 p-4 rounded-lg text-xs whitespace-pre-wrap break-words overflow-x-auto">
                  <code>{JSON.stringify(selectedDebugInfo, null, 2)}</code>
                </pre>
              </div>

              {/* Timestamp Information */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Timing Information</h3>
                <div className="bg-muted p-3 rounded-lg text-xs">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">Query ID:</span>
                      <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                        {selectedDebugInfo.query_id}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Timestamp:</span>
                      <div className="text-gray-600 dark:text-gray-400">
                        {new Date(selectedDebugInfo.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Query Type:</span>
                      <div className="text-gray-600 dark:text-gray-400">
                        {selectedDebugInfo.query_type}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">User ID:</span>
                      <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                        {selectedDebugInfo.user_id || 'Not available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
